/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable dot-notation ,radix */
import { CSVLink } from 'react-csv';
import { useState, useEffect, useRef } from 'react';
import { models } from 'powerbi-client';
import { useSelector } from 'react-redux';
import { selectAuthUser } from 'modules/common/auth/selectors';
import { selectHotelName } from 'modules/dashboard/selectors';
import { isEmpty } from 'modules/common/helpers/object';
import { formatDate } from 'modules/dashboard/functions';
import {
  EXCEL_YEAR_MONTH_DAY_PATTERN,
  TIME_PATTERN,
  YEAR_MONTH_DAY_PATTERN,
} from 'modules/common/constants/date-range';
import { Grid, IconButton, Typography } from '@mui/material';
import { Loader } from 'modules/common/components';
import DownloadIcon from '@mui/icons-material/Download';
import NoDataDialog from 'modules/dashboard/components/tab-container/export-visual-data/components/no-data';
import * as XLSX from 'xlsx-js-style';
import GenerateCSV from './functions';
import { styleXlsxFiles } from './style-xlsx';
import mapper from './mapper';
import powerBIVisualPresets from './presets';

// Resolve the mapper/preset key for a visual using its stable internal name,
// falling back to the display title for reports where keys are title-based
const getExportFileName = (fileName, visual) => {
  const nameKey = `${fileName}_${visual?.name}`;
  if (mapper[nameKey] && powerBIVisualPresets[nameKey]) return nameKey;
  return `${fileName}_${visual?.title}`;
};

const extractAllMetaFromPBI = async (visualComponents) => {
  let detailsList = [];
  detailsList = await Promise.all(
    visualComponents.map(async (component) => {
      let data = await component.exportData(models.ExportDataType.Summarized);
      data = data.data.split('\r\n').filter((dataComponent) => dataComponent.includes(':'));
      return data[0];
    })
  );

  return detailsList;
};
// This function extracts the data from the Power BI report visual and formats it into a CSV string. It also extracts metadata from the visual components and includes it in the CSV. The function takes in the report object, file name, current user, and selected hotel as parameters.
export const getPbiExportData = async ({
  report,
  fileName,
  selectedHotel,
  isLoaded,
  metaData,
  visualTypes = ['pivotTable'],
}) => {
  if (!isEmpty(report) && isLoaded) {
    const pages = await report.getPages();
    const currentPage = pages.filter((page) => page.isActive)[0];
    const visuals = await currentPage.getVisuals();
    //
    const visualDetailsComponents = visuals.filter((visual) => visual.type === 'multiRowCard');
    const visualMeta = metaData ?? (await extractAllMetaFromPBI(visualDetailsComponents));

    let hotelNames = Array.from(
      document.getElementsByClassName('MuiGrid-root MuiGrid-item MuiGrid-grid-xs-12')
    );
    hotelNames = hotelNames.filter((node) =>
      node.parentElement.parentElement.parentElement.className.includes('container_paceVisualGrid')
    );
    if (hotelNames.length > 0) {
      let domNodeIndex = 0;
      if (hotelNames.length === 2) {
        domNodeIndex = fileName[0] === 'T' ? 0 : 1;
      }
      hotelNames = hotelNames[domNodeIndex].innerText;
      if (hotelNames) {
        hotelNames = hotelNames.replaceAll(':', '","').split('\n');
      }
    }
    const pivotTables = visuals.filter((v) => visualTypes.includes(v.type));

    if (!pivotTables.length) {
      throw new Error('No pivot table visuals found');
    }

    const exportedTables = await Promise.all(
      pivotTables.map(async (visual) => {
        const result = await visual?.exportData(models.ExportDataType.Summarized);
        if (!result?.data?.length) return null;
        const cleanedData = result.data.replaceAll(' 00:00:00', '');
        return cleanedData;
      })
    );
    //
    const finalTables = exportedTables.filter(Boolean);
    let meta = '';
    meta +=
      hotelNames.length === 2
        ? `"${hotelNames[0]}"\r\n"${hotelNames[1]}"\r\n`
        : `Hotel Name," ${selectedHotel?.label}"\r\n`;
    visualMeta.forEach((metaString) => {
      metaString.split(',').forEach((metaValue) => {
        if (metaValue === '""') return;
        meta += `${metaValue.split(':').join(',')}\r\n`;
      });
    });
    //
    return `${meta}\r\n${finalTables.join('\r\n')}`;
  }
  throw new Error('Visual is still loading');
};

/**
 * Export Data component to download visual data in csv format
 * @returns
 */
const ExportToCSV = ({
  report,
  isLoaded,
  fileName,
  exportExcel,
  batchExport = false,
  setData,
  metaData,
  visualTypes = ['pivotTable'],
}) => {
  const csvLink = useRef();
  //
  const currentUser = useSelector(selectAuthUser);
  const selectedHotel = useSelector(selectHotelName);
  //
  const [visualData, setVisualData] = useState([]);
  const [initiateDownload, setInitiateDownload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [isDialog, setIsDialog] = useState(false);
  //
  useEffect(() => {
    if (visualData?.length) {
      setInitiateDownload(true);
    }
  }, [visualData]);
  //
  useEffect(() => {
    if (initiateDownload) {
      if (exportExcel && !batchExport) {
        csvLink.current.link.click();
      }
      setInitiateDownload(false);
    }
    setLoading(false);
  }, [visualData, initiateDownload]);
  //
  const resetInitData = () => {
    setInitiateDownload(false);
    setLoading(false);
    setIsDialog(true);
  };
  //
  const exportPbiData = async () => {
    setLoading(true);
    if (!isEmpty(report) && isLoaded) {
      //
      try {
        const pages = await report.getPages();
        const currentPage = pages.filter((page) => page.isActive)[0];
        const visuals = await currentPage.getVisuals();
        //
        const visualDetailsComponents = visuals.filter((visual) => visual.type === 'multiRowCard');
        const visualMeta = metaData ?? (await extractAllMetaFromPBI(visualDetailsComponents));

        let hotelNames = Array.from(
          document.getElementsByClassName('MuiGrid-root MuiGrid-item MuiGrid-grid-xs-12')
        );
        hotelNames = hotelNames.filter((node) =>
          node.parentElement.parentElement.parentElement.className.includes(
            'container_paceVisualGrid'
          )
        );
        if (hotelNames.length > 0) {
          // decide the index to look by the first letter of the file fileName
          // as TV* refers to Trend and PV* refers to pace
          let domNodeIndex = 0;
          if (hotelNames.length === 2) {
            domNodeIndex = fileName[0] === 'T' ? 0 : 1;
          }
          hotelNames = hotelNames[domNodeIndex].innerText;
          if (hotelNames) {
            hotelNames = hotelNames.replaceAll(':', '","').split('\n');
          }
        }

        // process csv for each visual
        const processCSV = async (visual, exportFileName) => {
          const result = await visual?.exportData(models.ExportDataType.Summarized);
          if (result?.data?.length > 0) {
            result.data = result?.data.replaceAll(' 00:00:00', '');
            let meta = '';

            let csv = GenerateCSV(result, exportFileName);

            meta += `Username  , ${currentUser?.username}\r\n`;
            meta += `Report Generation Date  ," ${formatDate(
              new Date(),
              YEAR_MONTH_DAY_PATTERN
            )}, ${formatDate(new Date(), TIME_PATTERN)}"\r\n`;
            meta +=
              hotelNames.length === 2
                ? `"${hotelNames[0]}"\r\n"${hotelNames[1]}"\r\n`
                : `Hotel Name," ${selectedHotel?.label}"\r\n`;
            visualMeta.forEach((metaString) => {
              metaString.split(',').forEach((metaValue) => {
                if (metaValue === '""') return;
                meta += `${metaValue.split(':').join(',')}\r\n`;
              });
            });
            csv = `${meta} \r\n${csv} `;
            const sheet = XLSX.read(csv, {
              type: 'string',
              raw: true,
              dateNF: EXCEL_YEAR_MONTH_DAY_PATTERN,
            }).Sheets['Sheet1'];
            // export excel only if exportExcel is set to true
            if (exportExcel) {
              styleXlsxFiles(sheet, exportFileName, batchExport, setData);
              setLoading(false);
              setInitiateDownload(false);
            } else {
              setVisualData(csv);
            }
          } else {
            setMessage('No data available to export', resetInitData());
          }
        };

        const visualList = visuals.filter((v) => visualTypes.includes(v.type));

        if (visualList && visualList.length > 1) {
          visualList.forEach(async (visual) => {
            await processCSV(visual, getExportFileName(fileName, visual));
          });
        } else {
          await processCSV(visualList?.[0], fileName);
        }
      } catch (error) {
        setMessage('Something went wrong', resetInitData());
      }
    } else {
      setMessage('Visual is still loading', resetInitData());
    }
  };
  //
  useEffect(() => {
    if (batchExport) {
      exportPbiData();
    }
  }, [batchExport]);
  //
  return (
    <Loader loading={loading}>
      <Grid container direction="row" alignItems="center">
        <Typography
          sx={{
            mr: 1,
          }}
        >
          Export Data
        </Typography>
        <IconButton
          id="export-data-button"
          size="small"
          onClick={() => exportPbiData()}
          disabled={!isLoaded}
          sx={{
            '& .MuiSvgIcon-root': {
              width: '1.25rem',
              height: '1.25rem',
            },
          }}
        >
          <DownloadIcon />
        </IconButton>
      </Grid>
      <CSVLink
        asyncOnClick
        filename={`${fileName}_${formatDate(new Date(), YEAR_MONTH_DAY_PATTERN)}`}
        data={visualData}
        target="_self"
        ref={csvLink}
        sx={{ display: 'none' }}
      />
      <NoDataDialog open={isDialog} onClose={() => setIsDialog(false)} message={message} />
    </Loader>
  );
};
//
export default ExportToCSV;
